// Turns "how the windows should look" (the desired set) into the minimal set of
// human edit claims that realise it: for each region where the desired value
// differs from the current ledger, one claim asserting the desired value
// (loading heat/basket, or idle). Human claims outrank the seed and are the
// newest, so only the changed regions need re-stating — untouched spans keep
// following the ledger underneath.

export const GRID_MS = 10_000;

export const snapStartMs = (ms: number): number =>
  Math.floor(ms / GRID_MS) * GRID_MS;

export const snapEndMs = (ms: number): number =>
  Math.ceil(ms / GRID_MS) * GRID_MS;

export interface PlannedWindow {
  readonly bucket: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly heatId: string;
  readonly basketNumber: number;
}

export interface KeyedWindow extends PlannedWindow {
  readonly key: string;
}

export interface WindowClaim {
  readonly bucket: string;
  readonly startMs: number;
  readonly endMs: number;
  readonly targetStatus: "loading" | "idle";
  readonly heatId: string | null;
  readonly basketNumber: number | null;
}

type CellValue = {
  readonly heatId: string;
  readonly basketNumber: number;
} | null;

const valueAt = (
  windows: readonly PlannedWindow[],
  startMs: number,
  endMs: number
): CellValue => {
  const covering = windows.find(
    (window) => window.startMs <= startMs && window.endMs >= endMs
  );
  return covering === undefined
    ? null
    : { heatId: covering.heatId, basketNumber: covering.basketNumber };
};

const sameValue = (a: CellValue, b: CellValue): boolean =>
  a === null
    ? b === null
    : b !== null && a.heatId === b.heatId && a.basketNumber === b.basketNumber;

// Assumes non-overlapping windows within a bucket (the editor keeps the desired
// set non-overlapping via paintWindow, and compiled windows already are).
export const computeClaims = ({
  original,
  desired,
}: {
  readonly original: readonly PlannedWindow[];
  readonly desired: readonly PlannedWindow[];
}): WindowClaim[] => {
  const buckets = [
    ...new Set([...original, ...desired].map((window) => window.bucket)),
  ].sort();

  return buckets.flatMap((bucket): WindowClaim[] => {
    const originalWindows = original.filter(
      (window) => window.bucket === bucket
    );
    const desiredWindows = desired.filter((window) => window.bucket === bucket);
    const bounds = [
      ...new Set(
        [...originalWindows, ...desiredWindows].flatMap((window) => [
          window.startMs,
          window.endMs,
        ])
      ),
    ].sort((a, b) => a - b);

    const claims: WindowClaim[] = [];
    for (let index = 0; index + 1 < bounds.length; index++) {
      const startMs = bounds[index];
      const endMs = bounds[index + 1];
      if (startMs === undefined || endMs === undefined) continue;
      const desValue = valueAt(desiredWindows, startMs, endMs);
      if (sameValue(valueAt(originalWindows, startMs, endMs), desValue)) {
        continue;
      }

      const heatId = desValue?.heatId ?? null;
      const basketNumber = desValue?.basketNumber ?? null;
      const targetStatus = desValue === null ? "idle" : "loading";
      const last = claims[claims.length - 1];
      if (
        last?.endMs === startMs &&
        last.targetStatus === targetStatus &&
        last.heatId === heatId &&
        last.basketNumber === basketNumber
      ) {
        claims[claims.length - 1] = { ...last, endMs };
      } else {
        claims.push({
          bucket,
          startMs,
          endMs,
          targetStatus,
          heatId,
          basketNumber,
        });
      }
    }
    return claims;
  });
};

interface Interval {
  readonly startMs: number;
  readonly endMs: number;
}

const subtractFromOne = (
  base: Interval,
  cuts: readonly Interval[]
): Interval[] => {
  const pieces: Interval[] = [];
  let cursor = base.startMs;
  for (const cut of cuts) {
    if (cut.endMs <= base.startMs || cut.startMs >= base.endMs) continue;
    if (cut.startMs > cursor)
      pieces.push({ startMs: cursor, endMs: cut.startMs });
    cursor = Math.max(cursor, cut.endMs);
  }
  if (cursor < base.endMs) pieces.push({ startMs: cursor, endMs: base.endMs });
  return pieces;
};

// Insert or replace `edited` in the set, clipping any same-bucket window it
// overlaps so the result stays non-overlapping (the edited window wins). A
// straddled window splits into the two pieces either side.
export const paintWindow = (
  windows: readonly KeyedWindow[],
  edited: KeyedWindow
): KeyedWindow[] => {
  const clipped = windows
    .filter((window) => window.key !== edited.key)
    .flatMap((window): KeyedWindow[] => {
      if (window.bucket !== edited.bucket) return [window];
      return subtractFromOne(window, [edited]).map((piece, index) => ({
        ...window,
        key: index === 0 ? window.key : `${window.key}#${piece.startMs}`,
        startMs: piece.startMs,
        endMs: piece.endMs,
      }));
    });
  return [...clipped, edited];
};
